/**
 * Stale-bundle watcher. This app deploys many times a day; an open tab keeps
 * running the bundle it loaded, so freshly-shipped features look "broken"
 * until a refresh (bit Jeremy three times on 2026-06-10). This watches the
 * deployed index.html for a new asset hash:
 *   - checks on tab focus + every 3 minutes
 *   - when a new version is live: shows a refresh pill, and auto-reloads at
 *     the next ROUTE CHANGE (a safe moment — nothing in this app holds
 *     unsaved state across navigations)
 *   - sessionStorage guard so a CDN serving stale HTML can't reload-loop us
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// V2 NOTE (Codex): if code-splitting ever lands, deferred reload isn't enough —
// an old tab can 404 on a lazily-loaded chunk the deploy evicted. Add a global
// dynamic-import error handler that forces a reload (same once-per-version
// guard). Today the app is one bundle, so navigation-time reload is safe.
const CHECK_EVERY_MS = 3 * 60_000;
const RELOADED_KEY = "dd-reloaded-for";

function currentBundle(): string | null {
  const src = document.querySelector('script[src*="assets/index"]')?.getAttribute("src");
  const m = src ? /assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(src) : null;
  return m?.[1] ?? null;
}

async function deployedBundle(): Promise<string | null> {
  try {
    const res = await fetch(`/?ts=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    const m = /assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(html);
    return m?.[1] ?? null;
  } catch {
    // Offline / transient network error — just try again next tick.
    return null;
  }
}

export function VersionWatcher(): React.ReactElement | null {
  const [staleBundle, setStaleBundle] = useState<string | null>(null);
  const running = useRef(currentBundle());
  const location = useLocation();

  const check = useCallback(async () => {
    if (!running.current) return; // dev server — no hashed bundle to compare
    const live = await deployedBundle();
    if (live && live !== running.current) setStaleBundle(live);
  }, []);

  useEffect(() => {
    const onFocus = (): void => { void check(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const t = window.setInterval(() => void check(), CHECK_EVERY_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.clearInterval(t);
    };
  }, [check]);

  // Auto-heal at the next navigation — the user was leaving the view anyway.
  // The sessionStorage guard stops a reload loop if a CDN keeps serving the
  // old HTML: we only auto-reload ONCE per detected version.
  // Deliberately keyed on the ROUTE only — reading staleBundle from a ref so
  // a stale-detection mid-page never reloads until the user navigates.
  const staleRef = useRef<string | null>(null);
  staleRef.current = staleBundle;
  useEffect(() => {
    const stale = staleRef.current;
    if (stale && sessionStorage.getItem(RELOADED_KEY) !== stale) {
      sessionStorage.setItem(RELOADED_KEY, stale);
      window.location.reload();
    }
  }, [location.pathname]);

  if (!staleBundle) return null;
  return (
    <button
      onClick={() => window.location.reload()}
      style={{
        position: "fixed", bottom: 18, left: "50%", transform: "translateX(-50%)", zIndex: 200,
        display: "flex", alignItems: "center", gap: 8,
        background: "var(--accent)", color: "var(--accent-contrast)", border: "none",
        borderRadius: 999, padding: "9px 16px", fontSize: 12.5, fontWeight: 700,
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)", cursor: "pointer",
      }}
    >
      A newer version of the dashboard is live — click to refresh
    </button>
  );
}
